import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { GridCompResultData } from '../entities/gridCompResultData';
import { GridCompatibilityOrderProcessService } from '../services/grid-compatibility-order-process.service';
import { PrintService } from '../../+grid-application-shared/services/print.service';
import { FileService } from 'src/app/services/file.service';
import { GridApplicationProcessActionTypes } from '../../+grid-application-shared/entities/enums/grid-application-process-action-types.enum';
import { GridConnectionApplicationType, MessageType } from 'src/app/constants/app.constants';
import { SubmitService } from 'src/app/services/submit.service';
import { CommonService } from 'src/app/services/common.service';
import { GridCompatibilityService } from '../services/grid-compatibility.service';
import { Document } from '../../jurisdiction/jurisdiction.domain';

@Component({
  selector: 'app-result-form',
  templateUrl: './result-form.component.html',
  styleUrls: ['./result-form.component.scss'],
})
export class ResultFormComponent extends DestroyableComponent implements OnInit {
  applicationId$: Observable<number>;
  externalApplicationId$: Observable<string>;

  isLoading: boolean;

  resultData: GridCompResultData;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private service: GridCompatibilityOrderProcessService,
    public printService: PrintService,
    private fileService: FileService,
    private submitService: SubmitService,
    private commonService: CommonService,
    private gridCompatibilityService: GridCompatibilityService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.applicationId$ = this.route.paramMap.pipe(map(params => Number(params.get('id'))));

    this.externalApplicationId$ = this.applicationId$.pipe(
      switchMap(applicationId => this.gridCompatibilityService.getApplication(applicationId)),
      map(gridCompFeedInApplication => gridCompFeedInApplication.application_ext_id)
    );

    this.preventLeak(this.externalApplicationId$).subscribe(() => (this.isLoading = false));
  }

  saveResultData(applicationId: string) {
    this.isLoading = true;
    const sitePlanUpload$: Observable<number | null> = this.resultData.sitePlan['file']
      ? this.gridCompatibilityService.uploadDocuments([this.resultData.sitePlan as unknown as Document], applicationId)
      : of(null);

    const gridAssessmentDocumentUpload$: Observable<number | null> = this.resultData.gridAssessmentDocument['file']
      ? this.gridCompatibilityService.uploadDocuments([this.resultData.gridAssessmentDocument as unknown as Document], applicationId)
      : of(null);

    forkJoin([sitePlanUpload$, gridAssessmentDocumentUpload$])
      .pipe(
        switchMap(([sitePlanDocumentsUploaded, gridAssessmentDocumentsUploaded]) => {
          const sitePlanDocumentId = sitePlanDocumentsUploaded && sitePlanDocumentsUploaded[0]?.DocumentId;
          const gridAssessmentDocumentId = gridAssessmentDocumentsUploaded && gridAssessmentDocumentsUploaded[0]?.DocumentId;
          if (sitePlanDocumentId) {
            this.resultData.sitePlan.documentId = sitePlanDocumentId;
            this.resultData.sitePlan.contentType = this.resultData.sitePlan['file']['type'];
          } else {
            this.resultData.sitePlan.documentId = this.resultData.sitePlan['document_id'];
            this.resultData.sitePlan.contentType = this.resultData.sitePlan['content_type'];
          }

          if (gridAssessmentDocumentId) {
            this.resultData.gridAssessmentDocument.documentId = gridAssessmentDocumentId;
            this.resultData.gridAssessmentDocument.contentType = this.resultData.gridAssessmentDocument['file']['type'];
          } else {
            this.resultData.gridAssessmentDocument.documentId = this.resultData.gridAssessmentDocument['document_id'];
            this.resultData.gridAssessmentDocument.contentType = this.resultData.gridAssessmentDocument['content_type'];
          }

          return this.service.saveResultData(this.resultData);
        })
      )
      .subscribe({
        complete: () => {
          this.isLoading = false;
          this.resultData = null;
        },
      });
  }

  print(applicationId: string) {
    this.isLoading = true;

    this.preventLeak(this.printService.printApplication(GridConnectionApplicationType.GridCompatibility, applicationId, true)).subscribe(
      () => (this.isLoading = false)
    );
  }

  public downloadAllDocuments(applicationId: string): void {
    this.preventLeak(this.fileService.getAllDocumentsByApplicationId(applicationId)).subscribe(response =>
      this.fileService.downloadZipDocument(response)
    );
  }

  public executedProcessAction(actionType: GridApplicationProcessActionTypes, applicationId: number) {
    if (actionType == GridApplicationProcessActionTypes.External) {
      this.navigateToSubmittedPage();
    }

    if (actionType == GridApplicationProcessActionTypes.Internal) {
      this.navigateToJurisdictionOverview(applicationId);
    }
  }

  private navigateToSubmittedPage() {
    this.submitService.setMessage(MessageType.REQUEST_SUBMISSION);
    this.router.navigate(['/submitted']);
  }

  private navigateToJurisdictionOverview(applicationId: number) {
    this.commonService.showHighlightedRow = applicationId;
    this.router.navigate(['/jurisdiction-overview']);
  }
}
